import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

const routeTitles: Record<string, { title: string; description: string }> = {
  '/': {
    title: 'Oil & Gas Conversions | Professional Engineering Calculators & Converters',
    description: 'Free oil and gas engineering calculators and conversion tools. Calculate gas choke rates, velocity, GOR2, shrinkage, and convert pressure, temperature, volume units instantly.'
  },
  '/calculators/gas-choke': {
    title: 'Gas Choke Rate Calculator | Oil & Gas Engineering',
    description: 'Calculate gas choke rates for optimal production control in oil and gas operations. Free professional tool for petroleum engineers.'
  },
  '/calculators/gas-velocity': {
    title: 'Gas Velocity Calculator | Oil & Gas Engineering',
    description: 'Calculate gas velocity in pipelines and wells. Free tool for petroleum engineers to optimize gas flow rates.'
  },
  '/calculators/gor2': {
    title: 'GOR2 Computation Calculator | Oil & Gas Engineering',
    description: 'Calculate Gas Oil Ratio (GOR2) for reservoir analysis. Professional petroleum engineering tool.'
  },
  '/calculators/combined-meter': {
    title: 'Combined Meter Factor Calculator | Oil & Gas Engineering',
    description: 'Calculate combined meter factors for accurate flow measurement. Essential tool for petroleum engineers.'
  },
  '/calculators/katz-shrinkage': {
    title: 'Katz Shrinkage Calculator | Oil & Gas Engineering',
    description: 'Calculate Katz shrinkage factor for natural gas liquids. Professional petroleum engineering calculator.'
  },
  '/calculators/shrinkage': {
    title: 'Shrinkage Calculator | Oil & Gas Engineering',
    description: 'Calculate oil and gas shrinkage factors for accurate volume measurements. Free petroleum engineering tool.'
  },
  '/calculators/specific-gravity': {
    title: 'Specific Gravity Calculator | Oil & Gas Engineering',
    description: 'Calculate specific gravity of oil and gas fluids. Essential tool for petroleum engineers.'
  },
  '/calculators/volume-correction': {
    title: 'Volume Correction Factor Calculator | Oil & Gas Engineering',
    description: 'Calculate volume correction factors for temperature and pressure variations. Professional petroleum tool.'
  },
  '/converters/pressure': {
    title: 'Pressure Converter | Oil & Gas Engineering',
    description: 'Convert between pressure units: PSI, bar, kPa, and more. Free pressure conversion tool for engineers.'
  },
  '/converters/temperature': {
    title: 'Temperature Converter | Oil & Gas Engineering',
    description: 'Convert between Celsius, Fahrenheit, and Kelvin. Free temperature conversion tool for engineers.'
  },
  '/converters/volume': {
    title: 'Volume Converter | Oil & Gas Engineering',
    description: 'Convert between volume units: barrels, gallons, liters, cubic meters. Free volume converter for petroleum engineers.'
  },
  '/converters/density': {
    title: 'Density Converter & API Gravity Calculator | Oil & Gas Engineering',
    description: 'Convert between density units (kg/m³, g/cm³, lb/ft³, ppg) and calculate API gravity, specific gravity, and pressure gradients. Free tool for petroleum engineers.'
  },
  '/calculators/kill-fluid': {
    title: 'Kill Fluid & Safety Margin Calculator – Well Intervention Engineers',
    description: 'Calculate kill fluid density requirements for safe well control operations. Determine hydrostatic pressure, required mud weight, and overbalance pressure to ensure safe well intervention activities. Critical for workover, completion, and well control operations.'
  },
  '/calculators/z-factor': {
    title: 'Gas Compressibility Z-Factor Calculator | Hall-Yarborough Correlation',
    description: 'Calculate gas compressibility factor (z-factor) using the Hall-Yarborough correlation. Essential tool for petroleum engineers working with real gas behavior, reservoir analysis, and gas flow calculations.'
  },
  '/blog': {
    title: 'Oil & Gas Engineering Blog | Industry News & Insights',
    description: 'Latest articles, tutorials, and insights on oil and gas engineering, petroleum calculations, and industry best practices.'
  },
  '/saved': {
    title: 'Saved Calculations | Oil & Gas Conversions',
    description: 'Access your saved oil and gas calculations. View and manage your calculation history.'
  },
  '/api/keys': {
    title: 'API Keys | Oil & Gas Conversions',
    description: 'Manage your API keys for programmatic access to oil and gas calculators.'
  },
  '/api/docs': {
    title: 'API Documentation | Oil & Gas Conversions',
    description: 'API documentation for oil and gas calculation endpoints. Integration guide for developers.'
  },
  '/api/test': {
    title: 'API Tester | Oil & Gas Conversions',
    description: 'Test API endpoints for oil and gas calculations. Interactive API testing tool.'
  }
}

export default function PageTitle() {
  const location = useLocation()

  useEffect(() => {
    const routeInfo = routeTitles[location.pathname] || routeTitles['/']
    const fullUrl = `https://oilgasconversions.com${location.pathname}`

    if (routeInfo) {
      document.title = routeInfo.title

      let metaDescription = document.querySelector('meta[name="description"]')
      if (!metaDescription) {
        metaDescription = document.createElement('meta')
        metaDescription.setAttribute('name', 'description')
        document.head.appendChild(metaDescription)
      }
      metaDescription.setAttribute('content', routeInfo.description)

      let canonical = document.querySelector('link[rel="canonical"]')
      if (!canonical) {
        canonical = document.createElement('link')
        canonical.setAttribute('rel', 'canonical')
        document.head.appendChild(canonical)
      }
      canonical.setAttribute('href', fullUrl)

      let ogUrl = document.querySelector('meta[property="og:url"]')
      if (!ogUrl) {
        ogUrl = document.createElement('meta')
        ogUrl.setAttribute('property', 'og:url')
        document.head.appendChild(ogUrl)
      }
      ogUrl.setAttribute('content', fullUrl)

      let ogTitle = document.querySelector('meta[property="og:title"]')
      if (ogTitle) {
        ogTitle.setAttribute('content', routeInfo.title)
      }

      let ogDescription = document.querySelector('meta[property="og:description"]')
      if (ogDescription) {
        ogDescription.setAttribute('content', routeInfo.description)
      }

      let twitterTitle = document.querySelector('meta[name="twitter:title"]')
      if (twitterTitle) {
        twitterTitle.setAttribute('content', routeInfo.title)
      }

      let twitterDescription = document.querySelector('meta[name="twitter:description"]')
      if (twitterDescription) {
        twitterDescription.setAttribute('content', routeInfo.description)
      }
    }
  }, [location.pathname])

  return null
}
